import type {
  RenderContainerNode,
  RenderNode,
} from "../../../util/src/layout-compiler/types.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

type Point = { x: number; y: number };

type StoredClipStyle = {
  clipPath: string;
  webkitClipPath: string;
};

const compiledClipStyleByElement = new WeakMap<HTMLElement, StoredClipStyle>();
const activeClipSyncCancelByOwnerId = new Map<string, () => void>();

const EMPTY_CLIP_PATH = "polygon(0% 0%, 0% 0%, 0% 0%)";

function applyClipPathToElement(element: HTMLElement, clipPath: string): void {
  element.style.clipPath = clipPath;
  element.style.setProperty("-webkit-clip-path", clipPath);
}

function findOwnerContainer(
  ownerNodeId: string,
  renderNodeByKey: Map<string, RenderNode>,
): RenderContainerNode | null {
  for (const node of renderNodeByKey.values()) {
    if (node.id === ownerNodeId && node.kind === "container") {
      return node;
    }
  }

  return null;
}

function collectClippedChildrenForOwner(
  ownerNodeId: string,
  renderNodeByKey: Map<string, RenderNode>,
): RenderNode[] {
  const owner = findOwnerContainer(ownerNodeId, renderNodeByKey);

  if (!owner || owner.layout === "flex") {
    return [];
  }

  return owner.children.filter(
    (child) =>
      child.clipRects?.some((clip) => clip.ownerNodeId === ownerNodeId) ?? true,
  );
}

/**
 * Returns whether the absolute container has direct children that use its clip rect.
 */
export function hasClippedChildrenForOwner(
  ownerNodeId: string,
  renderNodeByKey: Map<string, RenderNode>,
): boolean {
  return (
    collectClippedChildrenForOwner(ownerNodeId, renderNodeByKey).length > 0
  );
}

/**
 * Cancels an in-flight per-frame clip sync loop for the given container owner.
 */
export function cancelClipSyncForOwner(ownerNodeId: string): void {
  activeClipSyncCancelByOwnerId.get(ownerNodeId)?.();
  activeClipSyncCancelByOwnerId.delete(ownerNodeId);
}

/**
 * Recomputes clip-path on children clipped by an animated absolute container.
 * Only `clipPath` is updated; child position and transform are untouched.
 */
export function updateClippedChildrenForAnimatedContainer(
  ownerNodeId: string,
  animatedContainerElement: HTMLElement,
  _canvasElement: HTMLElement,
  _scale: number,
  runtimeElementByKey: Map<string, HTMLElement>,
  renderNodeByKey: Map<string, RenderNode>,
): void {
  const children = collectClippedChildrenForOwner(ownerNodeId, renderNodeByKey);

  if (children.length === 0) {
    return;
  }

  const containerQuad = getLiveElementQuad(animatedContainerElement);

  for (const childNode of children) {
    const childKey = childNode.key ?? childNode.id;
    const childElement = runtimeElementByKey.get(childKey);

    if (!childElement) {
      continue;
    }

    if (!containerQuad) {
      applyClipPathToElement(childElement, EMPTY_CLIP_PATH);
      continue;
    }

    const childQuad = getLiveElementQuad(childElement);

    if (!childQuad) {
      applyClipPathToElement(childElement, EMPTY_CLIP_PATH);
      continue;
    }

    const clipPath = buildPolygonClipPathFromQuads({
      childQuad,
      containerQuad,
    });

    applyClipPathToElement(childElement, clipPath);
  }
}

/**
 * Registers a cancel callback for the active clip-sync loop on a container owner.
 */
export function registerClipSyncCancelForOwner(
  ownerNodeId: string,
  cancel: () => void,
): void {
  activeClipSyncCancelByOwnerId.set(ownerNodeId, cancel);
}

/**
 * Restores compiled clip-path styles (e.g. React unmount or animation cancel).
 */
export function restoreCompiledClipPathsForOwner(
  ownerNodeId: string,
  runtimeElementByKey: Map<string, HTMLElement>,
  renderNodeByKey: Map<string, RenderNode>,
  scale: number,
): void {
  const children = collectClippedChildrenForOwner(ownerNodeId, renderNodeByKey);

  for (const renderNode of children) {
    const key = renderNode.key ?? renderNode.id;
    const childElement = runtimeElementByKey.get(key);

    if (!childElement) {
      continue;
    }

    const stored = compiledClipStyleByElement.get(childElement);

    if (stored) {
      applyClipPathToElement(childElement, stored.clipPath);
      continue;
    }

    const compiled = getRenderNodeClipStyle(renderNode, scale);

    if (compiled.clipPath) {
      storeCompiledClipStyle(childElement, renderNode, scale);
      applyClipPathToElement(childElement, compiled.clipPath);
    }
  }
}

function storeCompiledClipStyle(
  element: HTMLElement,
  renderNode: RenderNode,
  scale: number,
): void {
  if (compiledClipStyleByElement.has(element)) {
    return;
  }

  const compiled = getRenderNodeClipStyle(renderNode, scale);
  const clipPath = compiled.clipPath ?? "polygon(0% 0%, 0% 0%, 0% 0%)";

  compiledClipStyleByElement.set(element, {
    clipPath,
    webkitClipPath: compiled.WebkitClipPath ?? clipPath,
  });
}

// ---------------------------------------------------------------------------
// DOM geometry (live only - no layout node sizes, no style.width/height hacks)
// ---------------------------------------------------------------------------

/**
 * Border-box quad in viewport space, including WAAPI-interpolated transform/size.
 * Does not read offsetWidth, computed width/height, or mutate element styles.
 */
function getLiveElementQuad(element: HTMLElement): Point[] | null {
  const elementWithQuads = element as HTMLElement & {
    getBoxQuads?: () => DOMQuad[];
  };

  if (typeof elementWithQuads.getBoxQuads === "function") {
    const quads = elementWithQuads.getBoxQuads();
    const quad = quads[0];

    if (quad) {
      return [
        { x: quad.p1.x, y: quad.p1.y },
        { x: quad.p2.x, y: quad.p2.y },
        { x: quad.p3.x, y: quad.p3.y },
        { x: quad.p4.x, y: quad.p4.y },
      ];
    }
  }

  const rect = element.getBoundingClientRect();

  if (rect.width <= 0.5 || rect.height <= 0.5) {
    return null;
  }

  return [
    { x: rect.left, y: rect.top },
    { x: rect.right, y: rect.top },
    { x: rect.right, y: rect.bottom },
    { x: rect.left, y: rect.bottom },
  ];
}

// ---------------------------------------------------------------------------
// Polygon clipping
// ---------------------------------------------------------------------------

function polygonSignedArea(points: Point[]): number {
  let area = 0;

  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
}

function isInsideClipEdge(
  point: Point,
  edgeStart: Point,
  edgeEnd: Point,
  clipIsClockwise: boolean,
): boolean {
  const cross =
    (edgeEnd.x - edgeStart.x) * (point.y - edgeStart.y) -
    (edgeEnd.y - edgeStart.y) * (point.x - edgeStart.x);

  return clipIsClockwise ? cross <= 0 : cross >= 0;
}

function getLineIntersection(
  line1Start: Point,
  line1End: Point,
  line2Start: Point,
  line2End: Point,
): Point {
  const x1 = line1Start.x;
  const y1 = line1Start.y;
  const x2 = line1End.x;
  const y2 = line1End.y;
  const x3 = line2Start.x;
  const y3 = line2Start.y;
  const x4 = line2End.x;
  const y4 = line2End.y;

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (Math.abs(denominator) < 0.00001) {
    return line1End;
  }

  return {
    x:
      ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) /
      denominator,
    y:
      ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) /
      denominator,
  };
}

function clipPolygonByPolygon(
  subjectPolygon: Point[],
  clipPolygon: Point[],
): Point[] {
  let output = subjectPolygon;
  const clipIsClockwise = polygonSignedArea(clipPolygon) < 0;

  for (let index = 0; index < clipPolygon.length; index += 1) {
    const edgeStart = clipPolygon[index]!;
    const edgeEnd = clipPolygon[(index + 1) % clipPolygon.length]!;
    const input = output;
    output = [];

    if (input.length === 0) {
      break;
    }

    let previousPoint = input[input.length - 1]!;

    for (const currentPoint of input) {
      const currentInside = isInsideClipEdge(
        currentPoint,
        edgeStart,
        edgeEnd,
        clipIsClockwise,
      );
      const previousInside = isInsideClipEdge(
        previousPoint,
        edgeStart,
        edgeEnd,
        clipIsClockwise,
      );

      if (currentInside) {
        if (!previousInside) {
          output.push(
            getLineIntersection(
              previousPoint,
              currentPoint,
              edgeStart,
              edgeEnd,
            ),
          );
        }

        output.push(currentPoint);
      } else if (previousInside) {
        output.push(
          getLineIntersection(previousPoint, currentPoint, edgeStart, edgeEnd),
        );
      }

      previousPoint = currentPoint;
    }
  }

  return output;
}

function viewportPointToElementLocalPercent(
  viewportPoint: Point,
  childQuad: Point[],
): Point {
  const topLeft = childQuad[0]!;
  const topRight = childQuad[1]!;
  const bottomLeft = childQuad[3]!;

  const axisX = {
    x: topRight.x - topLeft.x,
    y: topRight.y - topLeft.y,
  };
  const axisY = {
    x: bottomLeft.x - topLeft.x,
    y: bottomLeft.y - topLeft.y,
  };

  const pointVector = {
    x: viewportPoint.x - topLeft.x,
    y: viewportPoint.y - topLeft.y,
  };

  const determinant = axisX.x * axisY.y - axisX.y * axisY.x;

  if (Math.abs(determinant) < 0.00001) {
    return { x: 0, y: 0 };
  }

  const localX =
    (pointVector.x * axisY.y - pointVector.y * axisY.x) / determinant;
  const localY =
    (axisX.x * pointVector.y - axisX.y * pointVector.x) / determinant;

  return {
    x: localX * 100,
    y: localY * 100,
  };
}

function buildPolygonClipPathFromQuads({
  childQuad,
  containerQuad,
}: {
  /** Canonical order: topLeft, topRight, bottomRight, bottomLeft (never reordered). */
  childQuad: Point[];
  containerQuad: Point[];
}): string {
  const intersection = clipPolygonByPolygon(childQuad, containerQuad);

  if (intersection.length < 3) {
    return EMPTY_CLIP_PATH;
  }

  const localPoints = intersection.map((point) =>
    viewportPointToElementLocalPercent(point, childQuad),
  );

  return `polygon(${localPoints
    .map((point) => `${point.x}% ${point.y}%`)
    .join(", ")})`;
}
